/**
 * @fileoverview Event Details Card component.
 * @summary Displays event details including date, venue, and status.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/EventDetailsCard
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useTranslation } from 'react-i18next'
import { formatDate } from '../../../utils/date.js'
import { getOneShotDaysRemaining, formatOneShotExpirationDate, shouldShowOneShotExpiration } from '../../../utils/oneShotHelpers.js'
import type { Event } from '../../../store/slices/eventsSlice'
import type { Venue } from '../../../store/slices/venuesSlice'

interface EventDetailsCardProps {
  isOpen: boolean
  event: Event
  selectedVenue: Venue | undefined
  getStatusBadgeClass: (status: Event['status']) => string
}

/**
 * @summary Get status badge color class.
 */
function getDefaultStatusBadgeClass(status: Event['status']): string {
  switch (status) {
    case 'draft':
      return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    case 'active':
      return 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
    case 'completed':
      return 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
    case 'cancelled':
      return 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
    default:
      return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  }
}

/**
 * @summary Event details card component.
 * @param {EventDetailsCardProps} props - Component props.
 * @returns {React.ReactElement} Event details card or null if not open.
 */
export function EventDetailsCard({
  isOpen,
  event,
  selectedVenue,
  getStatusBadgeClass: customGetStatusBadgeClass,
}: EventDetailsCardProps) {
  const { t, i18n } = useTranslation()
  const getStatusBadgeClass = customGetStatusBadgeClass || getDefaultStatusBadgeClass

  if (!isOpen) {
    return null
  }

  return (
    <div className="card mb-6">
      <div className="flex items-start gap-3 mb-2">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {/* Title moved to PageHeader, so just placeholder here */}
          </div>
          

          {event.description && (
            <p className="text-(--color-text-secondary) mb-3">{event.description}</p>
          )}
          <div className="space-y-2 text-sm text-(--color-text-secondary)">
            <p>
              <span className="font-medium">{t('events.date')}:</span>{' '}
              {formatDate(event.date, i18n.language)}
            </p>
            
            {selectedVenue && (
              <>
                <div className="flex flex-row justify-between">
                  <p>
                    <span className="font-medium">{t('events.venue')}:</span> {selectedVenue.name}
                    {selectedVenue.city && ` - ${selectedVenue.city}`}
                  </p>
                  <span className={`px-3 py-1 rounded-full my-auto text-xs font-semibold ${getStatusBadgeClass(event.status)}`}>
                    {t(`events.status${event.status.charAt(0).toUpperCase() + event.status.slice(1)}`)}
                  </span>
                </div>
              </>
            )}
            {/* ONE_SHOT Expiration Date */}
          {shouldShowOneShotExpiration(event) && (
              <p className={`text-sm ${event.status === 'completed' ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-green-700 dark:text-green-400'}`}>
                <span className="">{t('subscription.expiresLabel') || 'Expire le'}:</span>{' '}
                <span className={event.status === 'completed' 
                  ? 'line-through' 
                  : getOneShotDaysRemaining(event.oneShotExpiresAt) <= 3 ? 'text-orange-600 dark:text-orange-400 font-semibold' : ''}>
                  {formatOneShotExpirationDate(event.oneShotExpiresAt, i18n.language)}
                  {' '}
                  ({t('subscription.oneShotDaysRemaining', { count: getOneShotDaysRemaining(event.oneShotExpiresAt) })})
                </span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

